import { useAchievementTypes, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  AchievementAvailableActions,
  AchievementIcon,
  AchievementRenderAddedBy,
  AchievementRenderTemplate,
  AchievementSimpleStatus,
  AchievementCompletionAddSelf,
  AchievementUserPreviousCompletions,
  AchievementEditAttachmentSelf
} from 'components/achievements';
import {
  Badge,
  ContentLabel,
  ContentValue,
  DisplayAttachments,
  DisplayFormattedDate,
  FlexContainer,
  Link,
  Owners,
  RichTextValue,
  TextWithLineBreaks,
  User
} from 'components/common';
import { Box } from 'components/common/box';
import {
  AttributeDisplayValue,
  DisplayAttributeInProductPage
} from 'components/common/customAttributes';
import { CompetenciesProductBadge } from 'components/competencies';

import type { GetAchievementsRes } from 'shared-types/responses';

interface Props {
  achievement: GetAchievementsRes['achievements'][0];
  updateAchievement: () => Promise<void>;
}

const AchievementRender = ({ achievement, updateAchievement }: Props) => {
  const { language, messages, serverConfig, theme, user } = useReduxState();

  const { title } = useAchievementTypes({ type: achievement?.type });

  const completion = achievement?.completions?.[0] ?? null;

  return !achievement ? null : (
    <div className="achievement wrapper">
      <Box padding={0}>
        <div className="inner">
          <div className="header">
            <div className="icon">
              <AchievementIcon size="large" type={achievement.type} />
            </div>

            <div className="type">{title}</div>

            <div className="header-content">
              <div className="title">{achievement.title}</div>

              {achievement.description ? (
                <div className="description">
                  <RichTextValue html={achievement.description} />
                </div>
              ) : null}
            </div>
          </div>

          <div className="info">
            <DisplayAttributeInProductPage
              customAttributes={achievement.customAttributes}
              position="at start"
              productType={achievement.type}
            />

            {completion?.members?.length ? (
              <ContentLabel
                horizontal={true}
                label={messages.commonParticipants}
              >
                <FlexContainer style={{ gap: 10 }}>
                  {completion?.members.map((member) => (
                    <User key={member.id} size="small" user={member} />
                  ))}
                </FlexContainer>
              </ContentLabel>
            ) : null}

            <ContentLabel horizontal={true} label={messages.commonStatus}>
              <AchievementSimpleStatus
                expirationDate={completion?.expirationDate}
                isPendingRequest={Boolean(achievement.pendingRequest)}
                isInactive={completion?.isInactive}
                status={
                  completion?.status ||
                  (achievement.isPathAchievement ? 'not started' : undefined)
                }
                type={achievement.type}
              />
            </ContentLabel>

            {completion?.status === 'completed' ? (
              <ContentLabel
                horizontal={true}
                label={messages.completionsCompletionDate}
                style={{ whiteSpace: 'nowrap' }}
              >
                <ContentValue style={{ fontWeight: 600 }}>
                  <DisplayFormattedDate date={completion.completionDate} />
                </ContentValue>
              </ContentLabel>
            ) : null}

            {completion?.expirationDate ? (
              <ContentLabel
                horizontal={true}
                label={messages.commonExpirationDate}
                style={{ whiteSpace: 'nowrap' }}
              >
                <ContentValue style={{ fontWeight: 600 }}>
                  <DisplayFormattedDate date={completion.expirationDate} />
                </ContentValue>
              </ContentLabel>
            ) : null}

            {completion?.addedBy ? (
              <ContentLabel horizontal={true} label={messages.commonAddedBy}>
                <ContentValue style={{ fontWeight: 600 }}>
                  <AchievementRenderAddedBy addedBy={completion.addedBy} />
                </ContentValue>
              </ContentLabel>
            ) : null}

            {completion?.comments ? (
              <ContentLabel horizontal={true} label={messages.commonComment}>
                <div className="comment">
                  <TextWithLineBreaks text={completion.comments} />
                </div>
              </ContentLabel>
            ) : null}

            {completion && achievement.certificateAllowDownloading ? (
              <ContentLabel
                horizontal={true}
                label={messages.commonAttachments}
              >
                <DisplayAttachments
                  attachments={completion.attachments || []}
                  isConfidential={!completion.canSeeAttachments}
                  renderInPortal={false}
                />
              </ContentLabel>
            ) : null}

            {achievement.userAttributes?.length
              ? achievement.userAttributes.map((attribute) => (
                  <ContentLabel
                    horizontal={true}
                    key={attribute.id}
                    label={
                      attribute.titleTranslations?.[language] || attribute.title
                    }
                  >
                    <ContentValue style={{ fontWeight: 600 }}>
                      <AttributeDisplayValue
                        value={completion?.userAttributes?.[attribute.id]}
                      />
                    </ContentValue>
                  </ContentLabel>
                ))
              : null}

            {achievement.categories?.length ? (
              <ContentLabel
                horizontal={true}
                label={messages.commonCategories}
                spacing={20}
              >
                <ContentValue>
                  <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                    {achievement.categories.map((category) => (
                      <Badge key={category} size="medium" text={category} />
                    ))}
                  </FlexContainer>
                </ContentValue>
              </ContentLabel>
            ) : null}

            {achievement.competencies.length ? (
              <ContentLabel
                horizontal={true}
                label={messages.competenciesGranted}
                style={{ whiteSpace: 'nowrap' }}
              >
                <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                  {achievement.competencies.map((competency) => (
                    <CompetenciesProductBadge
                      key={`${competency.id}${
                        competency.level ? `-${competency.level.id}` : ''
                      }`}
                      competency={competency}
                    />
                  ))}
                </FlexContainer>
              </ContentLabel>
            ) : null}

            {!serverConfig.productHideOwner && (
              <ContentLabel
                horizontal={true}
                label={messages.commonOwners}
                spacing={20}
                style={{ whiteSpace: 'nowrap' }}
              >
                {achievement.owners?.length ? (
                  <FlexContainer style={{ gap: 5 }}>
                    <User
                      showNameOrUsername={false}
                      size="small"
                      user={achievement.owners?.[0]}
                    />

                    <Owners owners={achievement.owners} />
                  </FlexContainer>
                ) : (
                  messages.commonNoOwners
                )}
              </ContentLabel>
            )}

            {achievement.externalUrl && (
              <ContentLabel
                horizontal={true}
                label={messages.achievementExternalUrl}
                style={{ whiteSpace: 'nowrap' }}
              >
                <ContentValue>
                  <Link
                    external={true}
                    href={achievement.externalUrl}
                    openInNewTab={true}
                    text={achievement.externalUrl}
                  />
                </ContentValue>
              </ContentLabel>
            )}

            <DisplayAttributeInProductPage
              customAttributes={achievement.customAttributes}
              productType={achievement.type}
            />
          </div>
        </div>

        {(!completion?.status || achievement.pendingRequest) &&
        achievement?.isAvailableAchievement ? (
          <AchievementAvailableActions
            achievement={achievement}
            isNewRequest={Boolean(
              achievement.pendingRequest && completion?.status
            )}
            onUpdate={updateAchievement}
            preventRedirectAfterSubmit={true}
          />
        ) : completion?.status === 'completed' &&
          (achievement?.isAvailableAchievement ||
            achievement?.certificateAllowAdding) ? (
          <div className="actions">
            {achievement.certificateAllowAdding ? (
              <AchievementEditAttachmentSelf
                achievement={achievement}
                updateAchievement={updateAchievement}
              />
            ) : null}

            {achievement?.isAvailableAchievement ? (
              <AchievementCompletionAddSelf
                achievement={achievement}
                isRequest={achievement.userAccessRequireApproval}
                preventRedirectAfterSubmit={true}
                updateAchievements={updateAchievement}
                userHasPreviousCompletions={true}
              />
            ) : null}
          </div>
        ) : null}
      </Box>

      <AchievementUserPreviousCompletions
        achievement={achievement}
        previousCompletions={achievement?.previousCompletions}
        style={{ marginTop: 30 }}
        userId={user?.id}
      />

      {achievement.template.length > 0 &&
      (completion?.status === 'completed' ||
        achievement.isIncompleteViewableByUser) ? (
        <div className="items">
          <AchievementRenderTemplate
            achievement={achievement}
            updateAchievement={updateAchievement}
          />
        </div>
      ) : null}

      <style jsx>{`
        .achievement {
          margin: 0 auto;
          max-width: 720px;
        }
        .inner {
          padding: 15px;
        }
        .header {
          text-align: center;
        }
        .type {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-style: italic;
          font-weight: 600;
        }
        .header-content {
          margin-top: 25px;
        }
        .title {
          color: ${theme.textColor};
          font-size: 2rem;
          font-weight: 700;
          margin: 20px 0;
        }
        .description {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          line-height: 1.5;
          text-align: justify;
        }
        .info {
          margin-top: 30px;
        }
        .comment {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          font-size: 1.4rem;
          font-weight: 400;
          line-height: 1.5;
          padding: 8px 12px;
        }
        .actions {
          align-items: center;
          border-top: 1px solid ${theme.lightGrey};
          display: flex;
          justify-content: flex-end;
          flex-grow: 0;
          flex-shrink: 0;
          gap: 10px;
          padding: 15px;
        }
        .items {
          margin-top: 30px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .inner {
            padding: 25px;
          }
        }
      `}</style>
    </div>
  );
};

export default AchievementRender;
